import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { STAGE_DEFINITIONS, type Dashboard } from '@tailoring-crm/shared';
import { formatNumber } from '../../lib/format.js';
import { cn } from '@tailoring-crm/ui';
import { AnalyticsPanel } from './analytics-panel.js';
import { STAGE_VISUALS, TONE_CLASSES } from './visuals.js';

/**
 * Work in hand, as a ring split by stage.
 *
 * A ring rather than bars because the question here is a share — "how much of what is in the
 * shop is still waiting to be cut?" — and the stage colours run a ramp from slate at booking to
 * green on the rail, so the ring reads cool-heavy when the work is early and warm-to-green when
 * it is nearly out of the door, before a single label is read.
 *
 * The legend IS the data. Every stage is a row with its count and its oldest garment, and every
 * row still links to the board filtered to that stage, exactly as the list this replaced did. So
 * the ring itself is `aria-hidden` decoration, as every chart in this app is: a screen reader gets
 * the list, the eye gets the ring and the list, and the two are drawn from the same rows.
 *
 * Counts only, never money, so it shows for everybody who can see Home.
 */

/** The ring's geometry, in viewBox units. */
const RADIUS = 40;
const THICKNESS = 14;
/** A hairline between neighbouring segments, so two slate stages do not merge into one. */
const GAP = 0.8;

interface Segment {
  stage: Dashboard['workload'][number]['stage'];
  /** Where the segment starts and how long it is, as a share of 100. */
  start: number;
  length: number;
}

function segmentsOf(workload: Dashboard['workload'], total: number): Segment[] {
  if (total === 0) return [];
  const segments: Segment[] = [];
  let start = 0;
  for (const row of workload) {
    if (row.items <= 0) continue;
    const length = (row.items / total) * 100;
    segments.push({ stage: row.stage, start, length });
    start += length;
  }
  return segments;
}

export function StageDonut({ workload }: { workload: Dashboard['workload'] }): ReactElement {
  const { t } = useTranslation();
  const total = workload.reduce((sum, row) => sum + row.items, 0);
  const segments = segmentsOf(workload, total);
  const gap = segments.length > 1 ? GAP : 0;

  return (
    <AnalyticsPanel title={t('dashboard.workload')}>
      <div className="relative mx-auto size-40">
        <svg
          aria-hidden="true"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          className="size-full"
          data-testid="stage-donut"
        >
          {/* The empty track: the whole ring when nothing is in hand, the gaps otherwise. */}
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            strokeWidth={THICKNESS}
            className="stroke-muted"
          />
          {/* Rotated so the first stage starts at twelve o'clock and the ring reads clockwise. */}
          <g transform="rotate(-90 50 50)">
            {segments.map((segment) => {
              const drawn = segment.length > gap * 2 ? segment.length - gap : segment.length;
              return (
                <circle
                  key={segment.stage}
                  data-stage={segment.stage}
                  cx="50"
                  cy="50"
                  r={RADIUS}
                  fill="none"
                  strokeWidth={THICKNESS}
                  pathLength={100}
                  strokeDasharray={`${drawn} ${100 - drawn}`}
                  strokeDashoffset={-segment.start}
                  className={TONE_CLASSES[STAGE_VISUALS[segment.stage].tone].stroke}
                />
              );
            })}
          </g>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-semibold tabular-nums" data-testid="donut-total">
            {formatNumber(total)}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('dashboard.analytics.itemsInHand', { count: total })}
          </span>
        </div>
      </div>

      <ul className="flex flex-col gap-1">
        {workload.map((row) => {
          const { icon: Icon, tone } = STAGE_VISUALS[row.stage];
          return (
            <li key={row.stage} data-stage={row.stage}>
              <Link
                to={`/board?stage=${row.stage}`}
                className="flex items-center gap-3 rounded-lg p-2 text-sm hover:bg-accent"
              >
                <span
                  data-testid="stage-chip"
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded-md',
                    TONE_CLASSES[tone].chip,
                  )}
                >
                  <Icon aria-hidden="true" className="size-3.5" />
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {t(`stage.${row.stage}`, {
                    defaultValue: STAGE_DEFINITIONS[row.stage].defaultLabel,
                  })}
                </span>
                {row.oldestDays > 0 ? (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t('dashboard.oldestDays', { count: row.oldestDays })}
                  </span>
                ) : null}
                <span className="w-8 shrink-0 text-right font-semibold tabular-nums">
                  {formatNumber(row.items)}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </AnalyticsPanel>
  );
}
